import { Banner, Button } from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { isEmpty } from "@s-libs/micro-dash";
import { useEffect, useRef, useState } from "react";

import AiSparkIconSVG from "@/icons/AiSparkIcon/AiSparkIconSVG";
import { IStandard } from "@/search/Langston/types/IStandard";
import ContentLevel from "@/types/models/ContentLevel.interface";
import { useGetUser } from "@/users/v3";
import useLessonPlannerValidation from "../useLessonPlannerValidation";
import AllottedTimeButtonGroup, {
  AllottedTimeValue,
} from "./AllottedTimeButtonGroup";
import LearningObjectiveTextArea from "./LearningObjectiveTextArea";
import LessonPlan from "./LessonPlan/LessonPlan";
import { getStoredLesson } from "./LessonPlan/utils/lessonPlanStorage";
import LessonPlannerStandards from "./LessonPlannerStandards";
import {
  bannerCss,
  generateButtonCss,
  generateButtonWrapperCss,
  lessonPlannerFormCss,
} from "./LessonPlannerTool.style";
import SupportingMaterials from "./SupportingMaterials";

const ButtonWithTrackEvent = withTrackEvent(Button);

export interface LessonPlannerToolProps {
  currentLevel: ContentLevel;
  allottedTime: AllottedTimeValue;
  setAllottedTime: (value: AllottedTimeValue) => void;
  contentId: string;
  learningObjective: string;
  setLearningObjective: (value: string) => void;
  supportingMaterials: {
    includeProjectIdea: boolean;
    includeMultilingualLearners: boolean;
    includeAssessmentQuestions: boolean;
  };
  setSupportingMaterials: (value: {
    includeProjectIdea: boolean;
    includeMultilingualLearners: boolean;
    includeAssessmentQuestions: boolean;
  }) => void;
  selectedStandards: IStandard[];
  setSelectedStandards: (standards: IStandard[]) => void;
  setStandardsModalActive: (active: boolean) => void;
}

const LessonPlannerTool = ({
  currentLevel,
  allottedTime,
  contentId,
  setAllottedTime,
  learningObjective,
  setLearningObjective,
  supportingMaterials,
  setSupportingMaterials,
  selectedStandards,
  setSelectedStandards,
  setStandardsModalActive,
}: LessonPlannerToolProps) => {
  const [lessonPlanFormIsComplete, setLessonPlanFormIsComplete] =
    useState(false);
  const textAreaWrapperRef = useRef<HTMLDivElement>(null);

  const { data: user } = useGetUser();
  const savedLesson = getStoredLesson({ user, contentId });

  const { reset, validationErrors, validateFields } =
    useLessonPlannerValidation();

  // Auto-clear validation errors when fields are corrected
  useEffect(() => {
    if (learningObjective && validationErrors.learningObjective) {
      validateFields(learningObjective, selectedStandards);
    }
  }, [
    learningObjective,
    validationErrors.learningObjective,
    selectedStandards,
    validateFields,
  ]);

  useEffect(() => {
    if (selectedStandards.length > 0 && validationErrors.standards) {
      validateFields(learningObjective, selectedStandards);
    }
  }, [
    selectedStandards.length,
    validationErrors.standards,
    learningObjective,
    validateFields,
  ]);

  const handleGenerateLessonPlan = () => {
    const trimmedLearningObjective = (learningObjective ?? "").trim();
    const isValid = validateFields(trimmedLearningObjective, selectedStandards);

    if (isValid) {
      setLessonPlanFormIsComplete(true);
      return;
    }

    const textarea = textAreaWrapperRef.current?.querySelector(
      "textarea",
    ) as HTMLTextAreaElement | null;
    textarea?.focus();
    textAreaWrapperRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleBackToForm = () => {
    setLessonPlanFormIsComplete(false);
    reset();
  };

  // Focus the form when returning from lesson plan view
  useEffect(() => {
    if (!lessonPlanFormIsComplete && textAreaWrapperRef.current) {
      const textarea = textAreaWrapperRef.current.querySelector(
        "textarea",
      ) as HTMLTextAreaElement | null;
      if (textarea) {
        textarea.scrollIntoView({ behavior: "smooth", block: "start" });
        textarea.focus();
      }
    }
  }, [lessonPlanFormIsComplete]);

  if (lessonPlanFormIsComplete || !isEmpty(savedLesson)) {
    const trimmedLearningObjective = (learningObjective ?? "").trim();

    return (
      <LessonPlan
        contentId={contentId}
        currentLevel={currentLevel}
        setLessonPlanFormIsComplete={handleBackToForm}
        lessonPlanRequest={{
          contentId,
          learningObjective: trimmedLearningObjective,
          allottedTime,
          ...supportingMaterials,
          selectedStandards,
        }}
        reset={reset}
        extraData={{
          standards: selectedStandards,
          objective: trimmedLearningObjective,
        }}
        savedLesson={savedLesson}
        user={user}
      />
    );
  }

  const showValidationBanner = Boolean(
    validationErrors.standards || validationErrors.learningObjective,
  );
  return (
    <form
      aria-label="Lesson Planner Tool Form"
      data-qa-selector="lesson-planner-tool-form"
      onSubmit={(e) => e.preventDefault()} // avoid native submit
    >
      <div css={lessonPlannerFormCss}>
        {showValidationBanner && (
          <Banner
            __cssFor={bannerCss}
            bannerType={Banner.types.error}
            body="You must include a learning objective and at least one standard."
            isCenter={false}
            isDismissible={false}
          />
        )}

        <LearningObjectiveTextArea
          value={learningObjective}
          onChange={setLearningObjective}
          error={validationErrors.learningObjective}
          textAreaWrapperRef={textAreaWrapperRef}
        />

        <LessonPlannerStandards
          selectedStandards={selectedStandards}
          setSelectedStandards={setSelectedStandards}
          setStandardsModalActive={setStandardsModalActive}
          error={validationErrors.standards}
        />

        <SupportingMaterials
          value={supportingMaterials}
          onClick={setSupportingMaterials}
        />

        <AllottedTimeButtonGroup
          value={allottedTime}
          onChange={setAllottedTime}
        />
      </div>

      <div css={generateButtonWrapperCss}>
        <ButtonWithTrackEvent
          type="button" // do not submit the form
          onClick={handleGenerateLessonPlan}
          __cssFor={{ root: generateButtonCss }}
          dataProps={{ "data-qa-selector": "generate-lesson-plan-button" }}
          flavor={Button.flavors.primary}
          icon={{
            SvgComponent: AiSparkIconSVG,
            alignment: Button.iconAlignments.right,
          }}
          actionName={actionNames.interact}
          actionPrefix={actionPrefixes.initiate}
          objectType={objectTypes.content}
          streamProperties={{
            description: "User clicked to generate lesson plan",
          }}
        >
          Generate lesson plan
        </ButtonWithTrackEvent>
      </div>
    </form>
  );
};

export default LessonPlannerTool;
